import { RoundIconButton } from '@/form/FormComponents'
import { Icon } from '@/components/Icon'
import { useIsMobile, useIsTouch, useContainerSize } from '@/hooks/useIsMobile'
import { useMenu } from '@/components/Menu'
import { useScrollable } from '@/components/DashboardComponents'

// columns: [
// {
//   name: "Title",
//   min_width: undefined OR 300, defaults to 300 (how much space do auto-size columns want?)
//   width: undefined OR 200,
//   id: undefined or "string" (Required only if sortable. Used for storing sorting preference.)
//   hide_order: undefined OR 0 (0 is the first column to hide, 1 is the second, etc.),
//   sortable_group: undefined OR "string" (if defined, only one of these columns is visible at a time, determined by hide_order)
//   disabled: true OR false (if true, hidden and not sortable)
//   sortable: undefined OR [{ value: -1, name: "A to Z", arrow: "up", default: true }, { value: 1, name: "Z to A", arrow: "down" }],
// }
//]

// sort: [column id, sort value]

export const List = ({ fullPage, banner, icons = true, actions = false, hide_actions: hide_actions_prop = false, actions_width = 0, menu = true, columns, sort, setSort, folders, setFolders, hide_header = false, compact = false, children }) => {
  const { scrollToTop } = useScrollable();

  const { handleContextMenu, handleOnClick } = useMenu({
    menuItems: sort ? [
      { heading: "Sort by" },
      ...(columns.filter(c => c.sortable && !c.disabled).map(c => ({
        label: c.name,
        icon: sort[0] === c.id ? "check" : "empty",
        onClick: () => {
          if (sort[0] !== c.id) {
            setSort([c.id, c.sortable.find(s => s.default)?.value]);
            scrollToTop?.();
          }
        },
      }))),
      "separator",
      { heading: "Sort direction" },
      ...(columns.find(c => c.id === sort[0]).sortable.map(s => ({
        label: s.name,
        icon: sort[1] === s.value ? "check" : "empty",
        onClick: () => {
          if (s.value !== sort[1]) {
            setSort([sort[0], s.value]);
            scrollToTop?.();
          }
        },
      }))),
      ...(setFolders ? [
        "separator",
        { heading: "Folders" },
        {
          label: "On top",
          icon: folders ? "check" : "empty",
          onClick: () => {setFolders(true)},
        },
        {
          label: "Mixed with files",
          icon: !folders ? "check" : "empty",
          onClick: () => {setFolders(false)},
        }
      ] : [])
    ] : []
  });

  const is_mobile = useIsMobile();
  const is_touch = useIsTouch();
  let hide_actions = (is_mobile || is_touch || hide_actions_prop) && actions;
  const [ref, width] = useContainerSize();
  const gap = is_mobile ? 20 : 40;

  // Hide disabled columns. May have duplicates.
  let hide_columns = columns.map((c, index) => c.disabled ? index : null).filter(a => a !== null);

  // Hide columns that are in a sortable group that is not currently sorted
  const sortable_groups = [...new Set(columns.filter(c => c.sortable_group).map(c => c.sortable_group))];
  for (const sortable_group of sortable_groups) {
    if (columns.find(c => c.id === sort[0])?.sortable_group === sortable_group) {
      // This group is being sorted, so hide the non-sorted columns in this group
      hide_columns.push(...columns.filter(c => c.sortable_group === sortable_group && c.id !== sort[0]).map(c => columns.indexOf(c)));
    } else {
      // This group is not being sorted, so leave visible the one with the highest hide_order and hide the rest
      const highest_hide_order = columns.filter(c => c.sortable_group === sortable_group && !c.disabled).reduce((max, c) => c.hide_order > max.hide_order ? c : max, columns.filter(c => c.sortable_group === sortable_group)[0]).hide_order;
      hide_columns.push(...columns.filter(c => c.sortable_group === sortable_group && c.hide_order !== highest_hide_order).map(c => columns.indexOf(c)));
    }
  }

  // Calculate total available width for columns
  let available_width = width - (16*2) - (icons ? 42 : 0) - (menu ? 45 : 0);

  while (true) {
    // How much space do the auto-size columns want?
    let wanted_min_columns_width = columns.filter(c => !c.width).reduce((acc, c) => acc + (c.min_width || 300), 0);

    // How much space do all visible columns + actions want?
    let current_width = wanted_min_columns_width + columns.filter((c, index) => c.width && !hide_columns.includes(index)).reduce((acc, c) => acc + c.width + gap, 0) + ((actions && !hide_actions) ? actions_width : 0);

    // Does it fit?
    if (available_width >= current_width) {
      break;
    } else {
      // If it doesn't fit, try hiding the actions first
      if (!hide_actions && actions) {
        hide_actions = true
      } else {
        // Otherwise, see if we can hide columns
        let can_hide_columns = columns.filter((c, index) => c.width && c.hide_order !== undefined && !hide_columns.includes(index));
        if (can_hide_columns.length > 0) {
          // // Filter out the currently sorted column unless it's the only option left
          // let columns_to_consider = can_hide_columns.filter((c) => c.id !== sort[0]);
          // if (columns_to_consider.length === 0) {
          //   columns_to_consider = can_hide_columns;
          // }
          let columns_to_consider = can_hide_columns;
          
          let column_to_hide = columns_to_consider.reduce((min, c) => c.hide_order < min.hide_order ? c : min, columns_to_consider[0]);
          hide_columns.push(columns.indexOf(column_to_hide));
        } else {
          break;
        }
      }
    }
  }

  return (
    <div ref={ref} className={"scenario_list" + (fullPage ? " full_page_list" : "") + (hide_actions ? " hide_column_" + ((icons ? 1 : 0) + (columns.length)) : "") + (hide_columns.map(column => " hide_column_" + (column + (icons ? 1 : 0))).join("")) + (compact ? " scenario_list_compact" : "") } style={{ gridTemplateColumns: `${icons ? "max-content" : ""} ${columns.map((column, index) => hide_columns.includes(index) ? "" : (column.width ? ((column.width + gap) + "px") : "1fr")).join(" ")} ${(actions && !hide_actions) ? "max-content" : ""} ${menu ? "max-content" : ""}`, "--list_column_count": columns.filter((c, index) => !hide_columns.includes(index)).length }}>
      {banner ? (
        <div className="scenario_list_header_banner scroller_shadow_offset_height">
          {banner}
        </div>
      ) : null}
      {!hide_header && <div className="scenario_list_header" onContextMenu={sort ? handleContextMenu : undefined}>
        {icons && <div className="scenario_list_header_icon_container"></div>}
        {columns.map((column) => {
          return (
            column.sortable ? (
              <div key={column.name} className={`scenario_list_header_column ${!is_touch ? "clickable" : ""}`} tabIndex={0}
                onClick={!is_touch ? () => {
                  if (sort[0] === column.id) {
                    setSort([column.id, column.sortable[0].value === sort[1] ? column.sortable[1].value : column.sortable[0].value]);
                  } else {
                    setSort([column.id, column.sortable.find(s => s.default)?.value]);
                  }
                  scrollToTop?.();
                } : undefined}
              >{column.name}{sort[0] === column.id ? <div className="scenario_list_header_arrow"><Icon name={`arrow-${column.sortable.find(s => s.value === sort[1])?.arrow}-alt`}/></div> : null}</div>
            ) : (
              <div key={column.name} className="scenario_list_header_column">{column.name}</div>
            )
          )
        })}
        {actions && <div></div>}
        {menu && <div className="scenario_list_header_sort_container">{sort && <button className="scenario_list_header_sort_button" onClick={handleOnClick}><Icon name="sort"/></button>}</div>}
      </div>}
      {children}
    </div>
  )
}

export const ListItem = ({ onClick, icon, iconColor, iconShortcut = false, menu, hideMenu = false, disabled = false, className, children }) => {
  return (
    <div className={"scenario_item" + ((!onClick || disabled) ? " not_clickable" : "") + (className ? " " + className : "") + (disabled ? " disabled" : "")} onClick={!disabled ? onClick : undefined} onContextMenu={menu ? (!hideMenu ? menu.handleContextMenu : (event) => {event.preventDefault(); event.stopPropagation();}) : undefined} tabIndex={0}>
      {icon && <div className={"item_icon" + (iconShortcut ? " item_icon_shortcut" : "")}>
        <Icon name={icon} style={iconColor ? {fill: iconColor} : undefined}/>
        {iconShortcut && <Icon name="shortcut" className="item_icon_shortcut_icon"/>}
      </div>}
      {children}
      {menu && <div className="item_more">
        {!hideMenu && <RoundIconButton icon="dots-vertical" className="small" onClick={menu.handleOnClick} />}
      </div>}
    </div>
  )
}

export const ItemColumn = ({ children, className }) => {
  return (
    <div className={"item_column" + (className ? " " + className : "")}>
      {children}
    </div>
  )
}

export const ItemActions = ({ children }) => {
  return (
    <div className="item_actions">
      {children}
    </div>
  )
}